import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const BrandingContext = createContext({
  branding: null,
  version: null,
  loading: true,
  refresh: () => {},
});

const FALLBACK = {
  company_name: "Dream Home ERP",
  tagline: "Business Operating System for Architecture & Engineering Companies",
  logo_url: "",
  favicon_url: "",
  login_bg_url: "https://images.pexels.com/photos/4458205/pexels-photo-4458205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  primary_color: "#18181b",
  secondary_color: "#3f3f46",
  footer_text: "© 2026",
  support_email: "",
  app_version: "Alpha 0.1",
};

function applyBranding(b) {
  if (!b) return;
  const root = document.documentElement;
  if (b.primary_color) root.style.setProperty("--brand-primary", b.primary_color);
  if (b.secondary_color) root.style.setProperty("--brand-secondary", b.secondary_color);
  const name = b.company_name || FALLBACK.company_name;
  document.title = `${name} · ${b.tagline || ""}`.trim().replace(/·\s*$/, "").trim();
  if (b.favicon_url) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = b.favicon_url;
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(FALLBACK);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [{ data: b }, { data: v }] = await Promise.all([
        api.get("/branding"),
        api.get("/platform/version"),
      ]);
      const merged = { ...FALLBACK, ...b };
      setBranding(merged);
      setVersion(v);
      applyBranding(merged);
    } catch {
      applyBranding(FALLBACK);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ branding, version, loading, refresh }),
    [branding, version, loading, refresh],
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
